import { Query, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getMedicalProfileFileCacheKey,
    getMedicalProfileFileSearchCacheKey,
    onUpdateMedicalProfileFile,
    onReprocessComplete,
    getMedicalProfileFoldersCacheKey,
    getMedicalProfileFilesFilePollingCacheKey,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { MedicalProfileFileListItem } from '../../primary-record-types/apiTypes/medicalProfileFilesService';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';
import { useMemo } from 'react';

/**
 * Whether a file is still expected to change state, and therefore worth polling.
 *
 * 'not-started' belongs here: the upload response can be cached before the asynchronous processing
 * Lambda has run, and that Lambda may resolve the file straight to 'failed' without ever passing
 * through 'processing'. Excluding it means such a file is displayed as not-started until the next
 * navigation or refetch. Used both to pick the files to poll and to decide whether a poll observed a
 * transition, so the two cannot drift apart.
 */
const getIsProcessingState = (item: MedicalProfileFileListItem) =>
    item.processingStatus === 'processing' || item.processingStatus === 'not-started';

/**
 * How many polls a file may sit in 'not-started' before polling gives up on it.
 *
 * Unlike 'processing', 'not-started' can be terminal: file processing is deliberately skipped for test
 * users, so those records stay in it for good and an unbounded poll would run for as long as the screen
 * stays mounted. The state we are actually watching for is the brief window between the upload response
 * being cached and the processing Lambda picking the file up, so a short bound covers it. 'processing'
 * stays unbounded — a large PDF genuinely takes minutes.
 */
const NOT_STARTED_POLL_LIMIT = 6;

const intl = getIntl('useMedicalProfileFiles', {
    errorLoadingProfileFiles: 'Error loading profile files',
    createFileSuccess: 'File created successfully',
    createFileError: 'Error occurred while creating file',
    deleteFileSuccess: 'File deleted successfully',
    deleteFileError: 'Error occurred while deleting file',
    errorFetchingDownloadUrl: 'Error loading file',
    textSearchError: 'Error text searching files',
    fuzzySearchError: 'Error fuzzy searching files',
    vectorSearchError: 'Error semantic searching files',
    reprocessError: 'Error reprocessing file',
    reprocessSuccess: 'Started reprocessing file',
    createFolderSuccess: 'Folder created successfully',
    createFolderError: 'Error occurred while creating folder',
    deleteFolderSuccess: 'Folder deleted successfully',
    deleteFolderError: 'Error occurred while deleting folder',
    updateFolderSuccess: 'Folder updated successfully',
    updateFolderError: 'Error occurred while updating folder',
    moveFolderSuccess: 'Folder moved successfully',
    moveFolderError: 'Error occurred while moving folder',
    moveFileSuccess: 'File moved successfully',
    moveFileError: 'Error occurred while moving file',
    updateFileSuccess: 'File updated successfully',
    updateFileError: 'Error occurred while updating file',
    bulkCreateFoldersSuccess: '{count} folder{plural} created successfully',
    bulkCreateFoldersError: 'Error occurred while creating folders',
});

export type UseMedicalProfileFilesArgs = {
    medicalProfileId: string | null;
    medicalProfileFileId?: string;
    onCreateFileSuccess?: () => void;
    onDeleteFileSuccess?: () => void;
    onFetchedDownloadUrl?: (downloadUrl: string) => void;
    onCreateFolderSuccess?: (
        folder: APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderResponse,
    ) => void;
    onDeletedFolderSuccess?: () => void;
    search?: string;
    folderId?: string;
    onMoveFolderSuccess?: () => void;
    onMoveFileSuccess?: () => void;
    onReprocessSuccess?: () => void;
    onBulkCreateFoldersSuccess?: () => void;
};

export type UpdateFolderArgs = {
    folderId: string;
    folderName: string;
};

export type MoveFolderArgs = {
    folderId: string;
    newParentFolderId: string;
};

export type MoveFileArgs = {
    medicalProfileFileId: string;
    newParentFolderId: string;
};

export type UpdateMedicalProfileFileArgs = {
    medicalProfileFileId: string;
    fileName: string;
};

export type useMedicalProfileFilesReturnValue = {
    isLoadingProfileFiles: boolean;
    profileFiles: APITypes.medicalProfileFilesService.MedicalProfileFileListItem[];
    createFile: (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFilesRequest) => void;
    isCreatingFile: boolean;
    deleteFile: (medicalProfileFileId: string) => void;
    isDeletingFile: boolean;
    fetchDownloadUrl: (medicalProfileFileId: string) => void;
    isFetchingDownloadUrl: boolean;
    isLoadingFile: boolean;
    file: APITypes.medicalProfileFilesService.MedicalProfileFileListItem | null;
    textSearchResult: APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse | null;
    isTextSearching: boolean;
    fuzzySearchResult: APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse | null;
    isFuzzySearching: boolean;
    vectorSearchResult: APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse | null;
    isVectorSearching: boolean;
    reprocess: (medicalProfileFileId: string) => void;
    isReprocessing: boolean;
    folderMetadata: FolderMetadata | null;
    isLoadingFolderMetadata: boolean;
    createFolder: (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderRequest) => void;
    isCreatingFolder: boolean;
    bulkCreateFolders: (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderRequest[]) => void;
    isBulkCreatingFolders: boolean;
    deleteFolder: (folderId: string) => void;
    isDeletingFolder: boolean;
    onUpdateFolder: (args: UpdateFolderArgs) => void;
    isUpdatingFolder: boolean;
    onMoveFolder: (args: MoveFolderArgs) => void;
    isMovingFolder: boolean;
    onMoveFile: (args: MoveFileArgs) => void;
    isMovingFile: boolean;
    fileCount: number | null;
    updateFile: (args: UpdateMedicalProfileFileArgs) => void;
    isUpdatingFile: boolean;
};

export type RootFolderMetadata = {
    isRoot: true;
};

export type SubFolderMetadata = {
    isRoot: false;
    folder: APITypes.medicalProfileFilesService.GetMedicalProfileFileFolderResponse;
};

export type TargetFolderMetadata = RootFolderMetadata | SubFolderMetadata;

export type FolderMetadata = {
    descendants: APITypes.medicalProfileFilesService.GetMedicalProfileFileFolderImmediateDescendantsResponse;
    targetFolder: TargetFolderMetadata;
};

export default function useMedicalProfileFiles({
    medicalProfileId,
    medicalProfileFileId,
    onCreateFileSuccess,
    onDeleteFileSuccess,
    onFetchedDownloadUrl,
    onCreateFolderSuccess,
    onDeletedFolderSuccess,
    search,
    folderId,
    onMoveFolderSuccess,
    onMoveFileSuccess,
    onReprocessSuccess,
    onBulkCreateFoldersSuccess,
}: UseMedicalProfileFilesArgs): useMedicalProfileFilesReturnValue {
    const queryClient = useQueryClient();
    const notify = useNotify();
    const hasPermission = useHasPermission(medicalProfileId, 'files', 'view');

    const folderQuery = useQuery<FolderMetadata>({
        queryKey: getMedicalProfileFoldersCacheKey(medicalProfileId || '', folderId || 'root'),
        queryFn: async (): Promise<FolderMetadata> => {
            const queryFolderId = folderId || 'root';
            const descendantsResponse =
                await appAPI.get<APITypes.medicalProfileFilesService.GetMedicalProfileFileFolderImmediateDescendantsResponse>(
                    urls.api.medicalProfileFiles.folderDescendants(medicalProfileId!, queryFolderId),
                );
            const folderResponse: TargetFolderMetadata =
                queryFolderId === 'root'
                    ? { isRoot: true }
                    : {
                          isRoot: false,
                          folder: await appAPI.get<APITypes.medicalProfileFilesService.GetMedicalProfileFileFolderResponse>(
                              urls.api.medicalProfileFiles.folder(medicalProfileId!, queryFolderId),
                          ),
                      };
            return { descendants: descendantsResponse, targetFolder: folderResponse };
        },
        enabled: hasPermission && !!medicalProfileId,
    });
    const fileQuery = useQuery({
        queryKey: getMedicalProfileFileCacheKey(medicalProfileId || '', medicalProfileFileId || ''),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalProfileFilesService.GetMedicalProfileFileResponse>(
                urls.api.medicalProfileFiles.medicalProfileFile(medicalProfileId!, medicalProfileFileId || ''),
                {
                    // screens can stay mounted after a file is deleted.  Just ignore these errors to not pollute the console
                    ignoreErrorStatus: [404],
                },
            );
            return response?.medicalProfileFile || null;
        },
        enabled: !!medicalProfileFileId && hasPermission && !!medicalProfileId,
    });

    const processingFiles = [
        ...(fileQuery.data ? [fileQuery.data] : []),
        ...(folderQuery.data?.descendants.medicalProfileFiles || []),
    ].filter(getIsProcessingState);

    useQueries({
        queries: medicalProfileId
            ? processingFiles.map((file) => ({
                  queryKey: getMedicalProfileFilesFilePollingCacheKey(medicalProfileId, file.medicalProfileFileId),
                  queryFn: async () => {
                      const response =
                          await appAPI.get<APITypes.medicalProfileFilesService.GetMedicalProfileFileResponse>(
                              urls.api.medicalProfileFiles.medicalProfileFile(
                                  medicalProfileId,
                                  file.medicalProfileFileId,
                              ),
                          );

                      const getIsCompletedState = (
                          item: APITypes.medicalProfileFilesService.MedicalProfileFileListItem,
                      ) => item.processingStatus === 'processed' || item.processingStatus === 'failed';

                      if (getIsCompletedState(response.medicalProfileFile) && getIsProcessingState(file)) {
                          onReprocessComplete.resetCache({
                              medicalProfileId,
                              medicalProfileFileId: file.medicalProfileFileId,
                          })();
                      }

                      queryClient.setQueryData<FolderMetadata>(
                          getMedicalProfileFoldersCacheKey(medicalProfileId, folderId || 'root'),
                          (data) => {
                              if (!data) return data;

                              const newState: FolderMetadata = {
                                  ...data,
                                  descendants: {
                                      ...data.descendants,
                                      medicalProfileFiles: data.descendants.medicalProfileFiles.map((item) => {
                                          if (item.medicalProfileFileId === file.medicalProfileFileId) {
                                              return response.medicalProfileFile;
                                          }
                                          return item;
                                      }),
                                  },
                              };

                              return newState;
                          },
                      );

                      queryClient.setQueryData<MedicalProfileFileListItem>(
                          getMedicalProfileFileCacheKey(medicalProfileId, file.medicalProfileFileId),
                          response.medicalProfileFile,
                      );

                      return response.medicalProfileFile;
                  },
                  refetchInterval: (query: Query<MedicalProfileFileListItem>) =>
                      file.processingStatus === 'not-started' && query.state.dataUpdateCount >= NOT_STARTED_POLL_LIMIT
                          ? false
                          : 1000 * 10,
              }))
            : [],
    });

    const textSearchQuery = useQuery({
        queryKey: getMedicalProfileFileSearchCacheKey(medicalProfileId || '', search || '', 'text'),
        queryFn: async () => {
            const request: APITypes.medicalProfileFilesService.MedicalProfileFileSearch = {
                search: search || '',
                type: 'text',
            };
            const response = await appAPI.post<APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse>(
                urls.api.medicalProfileFiles.search(medicalProfileId!),
                {
                    body: request,
                },
            );
            return response;
        },
        enabled: !!search && !!medicalProfileId,
    });
    useQueryErrorNotify(textSearchQuery, intl('textSearchError'));

    const fuzzySearchQuery = useQuery({
        queryKey: getMedicalProfileFileSearchCacheKey(medicalProfileId || '', search || '', 'fuzzy'),
        queryFn: async () => {
            const request: APITypes.medicalProfileFilesService.MedicalProfileFileSearch = {
                search: search || '',
                type: 'fuzzy',
            };
            const response = await appAPI.post<APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse>(
                urls.api.medicalProfileFiles.search(medicalProfileId!),
                {
                    body: request,
                },
            );
            return response;
        },
        enabled: !!search && !!medicalProfileId,
    });
    useQueryErrorNotify(textSearchQuery, intl('fuzzySearchError'));

    const vectorSearchQuery = useQuery({
        queryKey: getMedicalProfileFileSearchCacheKey(medicalProfileId || '', search || '', 'vector'),
        queryFn: async () => {
            const request: APITypes.medicalProfileFilesService.MedicalProfileFileSearch = {
                search: search || '',
                type: 'vector',
            };
            const response = await appAPI.post<APITypes.medicalProfileFilesService.MedicalProfileFileSearchResponse>(
                urls.api.medicalProfileFiles.search(medicalProfileId!),
                {
                    body: request,
                },
            );
            return response;
        },
        enabled: !!search && !!medicalProfileId,
    });

    const createFileMutation = useMutation({
        mutationFn: async (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFilesRequest) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to create a file');
            }

            await appAPI.post(urls.api.medicalProfileFiles.medicalProfileFiles(medicalProfileId), {
                body: args,
            });
        },
        onError: () => {
            notify('error', intl('createFileError'));
        },
        onSuccess: () => {
            notify('success', intl('createFileSuccess'));
            onCreateFileSuccess?.();
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
        },
    });

    const deleteFileMutation = useMutation({
        mutationFn: async (medicalProfileFileId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to delete a file');
            }

            return await appAPI.del(
                urls.api.medicalProfileFiles.medicalProfileFile(medicalProfileId, medicalProfileFileId),
            );
        },
        onError: () => {
            notify('error', intl('deleteFileError'));
        },
        onSuccess: () => {
            notify('success', intl('deleteFileSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onDeleteFileSuccess?.();
        },
    });

    const updateFileMutation = useMutation({
        mutationFn: async (args: UpdateMedicalProfileFileArgs) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to update a file');
            }

            const body: APITypes.medicalProfileFilesService.UpdateMedicalProfileFileRequest = {
                fileName: args.fileName,
            };

            return await appAPI.put(
                urls.api.medicalProfileFiles.medicalProfileFile(medicalProfileId, args.medicalProfileFileId),
                {
                    body,
                },
            );
        },
        onError: () => {
            notify('error', intl('updateFileError'));
        },
        onSuccess: () => {
            notify('success', intl('updateFileSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
        },
    });

    const reprocessFileMutation = useMutation({
        mutationFn: async (medicalProfileFileId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to reprocess a file');
            }

            return await appAPI.post(urls.api.medicalProfileFiles.reprocess(medicalProfileId, medicalProfileFileId));
        },
        onError: () => {
            notify('error', intl('reprocessError'));
        },
        onSuccess: (_data, medicalProfileFileId) => {
            // Remove the file's polling query rather than letting the cache reset invalidate it. Query
            // state survives invalidation, so a reprocess would inherit the poll count already spent on
            // the previous attempt and hit NOT_STARTED_POLL_LIMIT immediately, leaving the new attempt's
            // transitions unobserved.
            queryClient.removeQueries({
                queryKey: getMedicalProfileFilesFilePollingCacheKey(medicalProfileId!, medicalProfileFileId),
            });
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            notify('success', intl('reprocessSuccess'));
            onReprocessSuccess?.();
        },
    });

    const fetchDownloadUrlMutation = useMutation({
        mutationFn: async (medicalProfileFileId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to fetch a download url');
            }

            const response = await appAPI.get<APITypes.medicalProfileFilesService.GetDownloadUrlResponse>(
                urls.api.medicalProfileFiles.medicalProfileFileDownloadUrl(medicalProfileId, medicalProfileFileId),
            );
            return response.downloadUrl;
        },
        onError: () => {
            notify('error', intl('errorFetchingDownloadUrl'));
        },
        onSuccess: (downloadUrl) => {
            onFetchedDownloadUrl?.(downloadUrl);
        },
    });

    const createFolderMutation = useMutation({
        mutationFn: async (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderRequest) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to create a folder');
            }

            return await appAPI.post<APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderResponse>(
                urls.api.medicalProfileFiles.profileFolders(medicalProfileId),
                {
                    body: args,
                },
            );
        },
        onError: () => {
            notify('error', intl('createFolderError'));
        },
        onSuccess: (data) => {
            notify('success', intl('createFolderSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onCreateFolderSuccess?.(data);
        },
    });

    const bulkCreateFoldersMutation = useMutation({
        mutationFn: async (args: APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderRequest[]) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to create a folder');
            }

            await Promise.all(
                args.map((args) =>
                    appAPI.post<APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderResponse>(
                        urls.api.medicalProfileFiles.profileFolders(medicalProfileId),
                        {
                            body: args,
                        },
                    ),
                ),
            );
        },
        onError: () => {
            notify('error', intl('bulkCreateFoldersError'));
        },
        onSuccess: (_data, args) => {
            notify(
                'success',
                intl('bulkCreateFoldersSuccess', { count: args.length, plural: args.length > 1 ? 's' : '' }),
            );
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onBulkCreateFoldersSuccess?.();
        },
    });

    const deleteFolderMutation = useMutation({
        mutationFn: async (folderId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to delete a folder');
            }

            return await appAPI.del(urls.api.medicalProfileFiles.folder(medicalProfileId, folderId));
        },
        onError: () => {
            notify('error', intl('deleteFolderError'));
        },
        onSuccess: () => {
            notify('success', intl('deleteFolderSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onDeletedFolderSuccess?.();
        },
    });

    const onUpdateFolderMutation = useMutation({
        mutationFn: async (args: UpdateFolderArgs) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to update a folder');
            }

            const body: APITypes.medicalProfileFilesService.UpdateMedicalProfileFileFolderRequest = {
                folderName: args.folderName,
            };
            return await appAPI.put(urls.api.medicalProfileFiles.folder(medicalProfileId, args.folderId), {
                body,
            });
        },
        onError: () => {
            notify('error', intl('updateFolderError'));
        },
        onSuccess: () => {
            notify('success', intl('updateFolderSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
        },
    });

    const moveFolderMutation = useMutation({
        mutationFn: async (args: MoveFolderArgs) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to move a folder');
            }

            const body: APITypes.medicalProfileFilesService.UpdateMedicalProfileFileFolderRequest = {
                parentMedicalProfileFileFolderId: args.newParentFolderId,
            };
            return await appAPI.put(urls.api.medicalProfileFiles.folder(medicalProfileId, args.folderId), {
                body,
            });
        },
        onError: () => {
            notify('error', intl('moveFolderError'));
        },
        onSuccess: () => {
            notify('success', intl('moveFolderSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onMoveFolderSuccess?.();
        },
    });

    const moveFileMutation = useMutation({
        mutationFn: async (args: MoveFileArgs) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to move a file');
            }

            const body: APITypes.medicalProfileFilesService.UpdateFileFolderRequest = {
                parentMedicalProfileFileFolderId: args.newParentFolderId,
            };
            return await appAPI.put(
                urls.api.medicalProfileFiles.updateFileFolder(medicalProfileId, args.medicalProfileFileId),
                {
                    body,
                },
            );
        },
        onError: () => {
            notify('error', intl('moveFileError'));
        },
        onSuccess: () => {
            notify('success', intl('moveFileSuccess'));
            onUpdateMedicalProfileFile.resetCache({ medicalProfileId: medicalProfileId! })();
            onMoveFileSuccess?.();
        },
    });

    const fileCount = useMemo(() => {
        if (!folderQuery.data) return null;

        const directDescendentCount = folderQuery.data?.descendants.medicalProfileFiles.length || null;
        return (folderQuery.data.descendants.medicalProfileFileFolders || []).reduce(
            (acc, folder) => (folder.folderName === 'Notes Files' ? acc : folder.descendantFilesCount + acc),
            directDescendentCount || 0,
        );
    }, [folderQuery.data]);

    return {
        isLoadingProfileFiles: folderQuery.isLoading,
        profileFiles: folderQuery.data?.descendants.medicalProfileFiles ?? [],
        createFile: createFileMutation.mutate,
        isCreatingFile: createFileMutation.isPending,
        deleteFile: deleteFileMutation.mutate,
        isDeletingFile: deleteFileMutation.isPending,
        fetchDownloadUrl: fetchDownloadUrlMutation.mutate,
        isFetchingDownloadUrl: fetchDownloadUrlMutation.isPending,
        isLoadingFile: fileQuery.isLoading,
        file: fileQuery.data || null,
        textSearchResult: textSearchQuery.data || null,
        isTextSearching: textSearchQuery.isLoading,
        fuzzySearchResult: fuzzySearchQuery.data || null,
        isFuzzySearching: fuzzySearchQuery.isLoading,
        vectorSearchResult: vectorSearchQuery.data || null,
        isVectorSearching: vectorSearchQuery.isLoading,
        reprocess: reprocessFileMutation.mutate,
        isReprocessing: reprocessFileMutation.isPending,
        folderMetadata: folderQuery.data || null,
        isLoadingFolderMetadata: folderQuery.isLoading,
        createFolder: createFolderMutation.mutate,
        isCreatingFolder: createFolderMutation.isPending,
        deleteFolder: deleteFolderMutation.mutate,
        isDeletingFolder: deleteFolderMutation.isPending,
        onUpdateFolder: onUpdateFolderMutation.mutate,
        isUpdatingFolder: onUpdateFolderMutation.isPending,
        onMoveFolder: moveFolderMutation.mutate,
        isMovingFolder: moveFolderMutation.isPending,
        onMoveFile: moveFileMutation.mutate,
        isMovingFile: moveFileMutation.isPending,
        fileCount,
        updateFile: updateFileMutation.mutate,
        isUpdatingFile: updateFileMutation.isPending,
        bulkCreateFolders: bulkCreateFoldersMutation.mutate,
        isBulkCreatingFolders: bulkCreateFoldersMutation.isPending,
    };
}
